/** Small geometry helpers for the line-art landmarks (all coordinates on a 200×200 grid). */

const r1 = (n: number) => Math.round(n * 10) / 10;

/** A tapered spire rising from `base` to a point at `top`. */
export function spire(c: number, top: number, w: number, base: number): string {
  return `M${c - w} ${base} V${top + 50} C${c - w} ${top + 30} ${c - 3} ${top + 12} ${c} ${top} C${c + 3} ${top + 12} ${c + w} ${top + 30} ${c + w} ${top + 50} V${base}`;
}

/** Battlements along a top edge at height `y`. */
export function crenels(x1: number, x2: number, y: number, s = 5): string {
  let d = `M${x1} ${y}`;
  let x = x1;
  let up = true;
  while (x < x2) {
    const nx = Math.min(x + s, x2);
    d += up ? ` V${y - s} H${nx} V${y}` : ` H${nx}`;
    x = nx;
    up = !up;
  }
  return d;
}

export function roundArch(cx: number, base: number, springY: number, r: number): string {
  return `M${cx - r} ${base} V${springY} A${r} ${r} 0 0 1 ${cx + r} ${springY} V${base}`;
}

export function pointedArch(cx: number, base: number, springY: number, r: number): string {
  return `M${cx - r} ${base} V${springY} Q${cx - r} ${r1(springY - r * 1.2)} ${cx} ${r1(springY - r * 1.6)} Q${cx + r} ${r1(springY - r * 1.2)} ${cx + r} ${springY} V${base}`;
}

/** Moorish horseshoe arch: a circle that closes in below its widest point. */
export function horseshoe(cx: number, cy: number, r: number, base: number): string {
  const dx = r1(r * 0.85);
  const dy = r1(r * 0.527);
  return `M${cx - dx} ${base} V${cy + dy} A${r} ${r} 0 1 1 ${cx + dx} ${cy + dy} V${base}`;
}

/** Alternating stone wedges (voussoirs) around an arch centre. Angles in degrees, SVG orientation. */
export function voussoirs(cx: number, cy: number, inner: number, outer: number, from = 150, to = 390, step = 20): string[] {
  const p = (r: number, a: number) => {
    const rad = (a * Math.PI) / 180;
    return `${r1(cx + r * Math.cos(rad))} ${r1(cy + r * Math.sin(rad))}`;
  };
  const out: string[] = [];
  for (let a = from; a < to; a += step) {
    const b = a + step;
    out.push(`M${p(inner, a)} L${p(outer, a)} A${outer} ${outer} 0 0 1 ${p(outer, b)} L${p(inner, b)} A${inner} ${inner} 0 0 0 ${p(inner, a)} Z`);
  }
  return out;
}

export function waves(x1: number, x2: number, y: number, step = 16, amp = 3): string {
  let d = `M${x1} ${y}`;
  for (let x = x1; x < x2; x += step) d += ` q${step / 2} ${-amp} ${step} 0`;
  return d;
}

/** Points evenly spaced on a circle. */
export function ring(cx: number, cy: number, r: number, count: number): [number, number][] {
  return Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2;
    return [r1(cx + r * Math.cos(a)), r1(cy + r * Math.sin(a))];
  });
}

/** y on the quadratic curve (50,34)→ctrl(100,150)→(150,34) at a given x — for suspension-bridge hangers. */
export function cableY(x: number): number {
  const t = (x - 50) / 100;
  return r1(34 * (1 - t) ** 2 + 300 * t * (1 - t) + 34 * t ** 2);
}
